import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import UserRemoveSmall from 'wix-ui-icons-common/UserRemoveSmall';
import EmailSendSmall from 'wix-ui-icons-common/EmailSendSmall';
import EditSmall from 'wix-ui-icons-common/EditSmall';
import LinkSmall from 'wix-ui-icons-common/LinkSmall';
import DismissSmall from 'wix-ui-icons-common/DismissSmall';
import { RouteChildrenProps, withRouter } from 'react-router-dom';
import { PermissionKey } from '../../services/server-types';
import { hasPermissionByLevel } from '../../services/permissions-helper';
import {
  InviteRowData,
  isInvitePending,
  isInviteRowData,
  isUserRowData,
  TeamMemberTableRowData
} from '../types';
import { TableActionCell } from 'wix-style-react';

interface IAppProps extends InjectedTranslateProps, RouteChildrenProps {
  rowData: TeamMemberTableRowData;
  permissions: Set<string>;
  siteLevel: boolean;
  isUserOwner: boolean;

  onRemoveRequest(id: string, isCurrentUser?: boolean): void;

  onResendRequest(id: string): void;

  onGetLink(link: string, id: string): void;

  onGetTransferOwnershipLink(): void;

  onResendTransferOwnership(): void;

  onCancelTransferOwnership(): void;

  reportOpenChangeRolesBI(id: string): void;
}

const getRowDataId = (rowData): string => isInviteRowData(rowData) ? rowData.inviteId : (isUserRowData(rowData) ? rowData.userId : rowData.accountId);

class TeamMemberActions extends React.Component<IAppProps> {

  onEditClick = () => {
    const { rowData, history, reportOpenChangeRolesBI } = this.props;
    const id = getRowDataId(rowData);
    reportOpenChangeRolesBI(id);
    return history.push(`/permissions/edit/${id}`);
  }

  shouldDisplayRemoveAction = () => {
    const { rowData, permissions, siteLevel } = this.props;
    if (!isInviteRowData(rowData)) { //Member
      return hasPermissionByLevel(siteLevel, PermissionKey.DELETE_MEMBER, permissions);
    } else { //Invitee
      return hasPermissionByLevel(siteLevel, PermissionKey.DELETE_INVITE, permissions);
    }
  }

  shouldDisplayDivider = () => {
    return this.shouldDisplayRemoveAction() && (this.shouldDisplayEditLink() || this.shouldDisplayResendLink() || this.shouldDisplayGetLink());
  }

  shouldDisplayGetLink = () => {
    const { rowData, permissions, siteLevel } = this.props;
    return isInvitePending(rowData) && hasPermissionByLevel(siteLevel, PermissionKey.SEND_INVITE, permissions);
  }

  shouldDisplayTransferOwnershipActions = () => {
    const { rowData, siteLevel, isUserOwner } = this.props;
    return siteLevel && isUserOwner && rowData.isPendingOwner;
  }

  shouldDisplayEditLink = () => {
    const { rowData, permissions, siteLevel } = this.props;
    if (!isInviteRowData(rowData)) {  //Member
      return hasPermissionByLevel(siteLevel, PermissionKey.CHANGE_MEMBER, permissions);
    } else { //Invitee
      return isInvitePending(rowData) && hasPermissionByLevel(siteLevel, PermissionKey.CHANGE_INVITE, permissions);
    }
  }

  shouldDisplayResendLink = () => {
    const { rowData, permissions, siteLevel } = this.props;
    return isInviteRowData(rowData) && hasPermissionByLevel(siteLevel, PermissionKey.SEND_INVITE, permissions);
  }

  removeFromTheTeam = () => {
    const { onRemoveRequest } = this.props;
    onRemoveRequest(this.getId());
  }

  getId = (): string => {
    const { rowData } = this.props;
    return isUserRowData(rowData) ? rowData.userId :
      (isInviteRowData(rowData) ? rowData.inviteId : rowData.accountId);
  }

  render() {

    const { rowData, onResendRequest, onGetLink, t, siteLevel, onResendTransferOwnership, onCancelTransferOwnership, onGetTransferOwnershipLink } = this.props;

    let actions;
    if (this.shouldDisplayTransferOwnershipActions()) {
      actions = [
        {
          dataHook: 'resend-transfer-email',
          text: t('list.teamMemberActions.resendTransferOwnership'),
          icon: <EmailSendSmall/>,
          onClick: onResendTransferOwnership,
        },
        {
          dataHook: 'copy-transfer-email',
          text: t('list.teamMemberActions.copyTransferOwnershipLink'),
          icon: <LinkSmall/>,
          onClick: onGetTransferOwnershipLink,
        },
        { divider: true },
        {
          dataHook: 'cancel-transfer',
          text: t('list.teamMemberActions.cancelTransferOwnership'),
          icon: <DismissSmall/>,
          onClick: onCancelTransferOwnership,
        }];
    } else {
      actions = [
        ...(this.shouldDisplayEditLink() ? [{
          dataHook: 'edit-role',
          text: t('list.teamMemberActions.edit'),
          icon: <EditSmall/>,
          onClick: this.onEditClick,
        }] : []),
        ...(this.shouldDisplayResendLink() ? [{
          dataHook: 'resend',
          text: t('list.teamMemberActions.resend'),
          icon: <EmailSendSmall/>,
          onClick: () => onResendRequest(this.getId())
        }] : []),
        ...(this.shouldDisplayGetLink() ? [{
          dataHook: 'get-link',
          text: t('list.teamMemberActions.getLink'),
          icon: <LinkSmall/>,
          onClick: () => onGetLink((rowData as InviteRowData).acceptLink, this.getId())
        }] : []),
        ...(this.shouldDisplayDivider() ? [{ divider: true }] : []),
        ...(this.shouldDisplayRemoveAction() ? [{
          dataHook: 'remove',
          text: t(siteLevel ? 'list.teamMemberActions.site.remove' : 'list.teamMemberActions.remove'),
          icon: <UserRemoveSmall/>,
          onClick: this.removeFromTheTeam
        }] : [])
      ];
    }

    return (
      <TableActionCell
        dataHook={`member-actions-${getRowDataId(rowData)}`}
        popoverMenuProps={{ placement: 'left', maxWidth: 300, zIndex: 10000 }}
        secondaryActions={actions as any}
      />
    );
  }
}

export default withRouter(translate()(TeamMemberActions));
